import { useState, useEffect } from "react";

/* ═══════════════════════════════════════════════════════════════
   EXIT INTENT POPUP — Triggers when mouse leaves viewport top
   Shows "Free Store Audit" offer to capture leads
   Triggers once per session, after 15 seconds on page
   ═══════════════════════════════════════════════════════════════ */

import { Lock, Zap, BarChart2, Search, Check } from "lucide-react";

const highlights = [
  {
    icon: Lock,
    color: "#16a34a", // green
    text: "100% Free",
  },
  {
    icon: Zap,
    color: "#f59e0b", // orange
    text: "Results in 60s",
  },
  {
    icon: BarChart2,
    color: "#2563eb", // blue
    text: "50+ Metrics",
  },
];

export default function ExitIntentPopup() {
  const [show, setShow] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");

  useEffect(() => {
    // Don't show if already shown this session
    if (sessionStorage.getItem("exitPopupShown")) return;

    let canShow = false;
    const timer = setTimeout(() => {
      canShow = true;
    }, 15000); // 15s delay

    const handleExit = (e) => {
      if (
        e.clientY <= 5 &&
        canShow &&
        !sessionStorage.getItem("exitPopupShown")
      ) {
        setShow(true);
        sessionStorage.setItem("exitPopupShown", "true");
      }
    };

    // Mobile: show on scroll up after delay
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      if (
        window.scrollY < lastScrollY - 100 &&
        canShow &&
        !sessionStorage.getItem("exitPopupShown")
      ) {
        setShow(true);
        sessionStorage.setItem("exitPopupShown", "true");
      }
      lastScrollY = window.scrollY;
    };

    document.addEventListener("mouseleave", handleExit);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("mouseleave", handleExit);
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timer);
    };
  }, []);

  const handleSubmit = () => {
    if (!email || !url) return;
    const apiBase =
      window.location.hostname === "localhost"
        ? "http://localhost:8081"
        : "/api";
    fetch(`${apiBase}/save-lead.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name,
        email,
        url,
        source: "exit-popup-audit",
        budget: "Free Audit",
      }),
    }).catch(() => {});
    setSubmitted(true);
  };

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        animation: "epFadeIn .3s ease",
        padding: 20,
      }}
      onClick={() => setShow(false)}
    >
      <style>{`
        @keyframes epFadeIn{from{opacity:0}to{opacity:1}}
        @keyframes epSlideUp{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      <div
        className="exit-popup-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 20,
          maxWidth: 480,
          width: "100%",
          overflow: "hidden",
          boxShadow: "0 24px 80px rgba(0,0,0,.25)",
          animation: "epSlideUp .4s ease",
        }}
      >
        {/* Header */}
        <div
          className="exit-popup-header"
          style={{
            background: "linear-gradient(135deg, #2563eb 0%, #1e3c72 100%)",
            padding: "36px 32px 28px",
            textAlign: "center",
            position: "relative",
          }}
        >
          <button
            onClick={() => setShow(false)}
            style={{
              position: "absolute",
              top: 12,
              right: 16,
              background: "rgba(255,255,255,.15)",
              border: "none",
              color: "#fff",
              width: 32,
              height: 32,
              borderRadius: "50%",
              fontSize: 18,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
          <div
            style={{
              fontSize: 40,
              marginBottom: 12,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Search size={40} color="#ffffff" />
          </div>
          <h2
            style={{
              fontSize: 26,
              color: "#fff",
              lineHeight: 1.2,
              marginBottom: 8,
            }}
          >
            Wait! Get Your Free
            <br />
            Store Audit
          </h2>
          <p
            style={{
              fontSize: 14,
              color: "rgba(255,255,255,.7)",
              lineHeight: 1.6,
            }}
          >
            Our expert team analyzes 50+ metrics and gives you a custom report —
            completely free.
          </p>
        </div>

        {/* Body */}
        <div className="exit-popup-body" style={{ padding: "28px 32px 32px" }}>
          {submitted ? (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <div
                style={{
                  fontSize: 40,
                  marginBottom: 12,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Check size={40} color="#16a34a" />
              </div>
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#1a1a1a",
                  marginBottom: 6,
                }}
              >
                Request Sent!
              </h3>
              <p style={{ fontSize: 14, color: "#666" }}>
                We'll reach out within 24 hours with your audit.
              </p>
              <button
                onClick={() => setShow(false)}
                style={{
                  marginTop: 16,
                  padding: "10px 24px",
                  borderRadius: 50,
                  background: "#2563eb",
                  color: "#fff",
                  border: "none",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
                <input
                  placeholder="Your Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: 10,
                    border: "1.5px solid #e5e7eb",
                    fontSize: 14,
                    outline: "none",
                    transition: "border-color .3s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#2563eb")}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                />
                <input
                  placeholder="Email Address *"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: 10,
                    border: "1.5px solid #e5e7eb",
                    fontSize: 14,
                    outline: "none",
                    transition: "border-color .3s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#2563eb")}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                />
                <input
                  placeholder="Your Store URL *"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: 10,
                    border: "1.5px solid #e5e7eb",
                    fontSize: 14,
                    outline: "none",
                    transition: "border-color .3s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#2563eb")}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={!email}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: 10,
                  background: email
                    ? "linear-gradient(135deg, #2563eb, #1e3c72)"
                    : "#ccc",
                  color: "#fff",
                  border: "none",
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: email ? "pointer" : "not-allowed",
                  transition: "all .3s",
                }}
              >
                Get My Free Audit →
              </button>

              <div
                className="exit-popup-trust-row"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 16,
                  marginTop: 16,
                }}
              >
                {highlights.map((h, i) => {
                  const Icon = h.icon;
                  return (
                    <span
                      key={i}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 11,
                        color: "#999",
                        marginRight: 12,
                      }}
                    >
                      <Icon size={12} color={h.color} />
                      {h.text}
                    </span>
                  );
                })}
              </div>

              <p
                style={{
                  fontSize: 11,
                  color: "#ccc",
                  textAlign: "center",
                  marginTop: 12,
                }}
              >
                No spam. Your data is never shared.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
